import { Address, JobLocation } from '../../types';
import type { AddressValidationResult } from '../../types/address-validation-types';

/** Maps validated job location to shipping address (UDOMI-980). */
export function jobLocationToShippingAddress(
  jobLocation: JobLocation,
  preserve?: Pick<Address, 'phone' | 'email'>
): Address {
  return {
    address1: jobLocation.address1 ?? '',
    address2: jobLocation.address2 ?? null,
    city: jobLocation.city ?? '',
    state: jobLocation.state ?? '',
    zip: jobLocation.zip ?? '',
    phone: preserve?.phone ?? null,
    email: preserve?.email ?? null,
  };
}

export function hasValidatedJobLocation(
  jobLocation: JobLocation | undefined | null
): jobLocation is JobLocation & { validationStatus: 'validated' } {
  return (
    jobLocation?.validationStatus === 'validated' &&
    typeof jobLocation.address1 === 'string' &&
    jobLocation.address1.trim() !== ''
  );
}

/**
 * Whether a job location carries usable jobsite data — either a complete address
 * (address1 + city + state + zip) or a valid coordinate pair. Used to enforce that
 * Pickup orders always record a jobsite, even when address validation is overridden.
 */
const normalizeAddressField = (value?: string | null): string => (value ?? '').trim().toLowerCase();

/** Whether a job location address matches another address (case-insensitive, trimmed). */
export function addressesMatchForJobLocation(
  jobLocation: {
    address1?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  },
  address: {
    address1?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  }
): boolean {
  return (
    normalizeAddressField(jobLocation.address1) === normalizeAddressField(address.address1) &&
    normalizeAddressField(jobLocation.city) === normalizeAddressField(address.city) &&
    normalizeAddressField(jobLocation.state) === normalizeAddressField(address.state) &&
    normalizeAddressField(jobLocation.zip) === normalizeAddressField(address.zip)
  );
}

/** Build a validated job location from a billing/shipping address and validation API result. */
export function buildValidatedJobLocationFromAddressValidation(
  sourceAddress: Address,
  validationResult: AddressValidationResult
): JobLocation {
  const resolved =
    validationResult.hasSuggestion && validationResult.suggestedAddress
      ? validationResult.suggestedAddress
      : validationResult.validatedAddress;

  return {
    address1: resolved?.address1 ?? sourceAddress.address1 ?? '',
    address2: resolved?.address2 ?? sourceAddress.address2 ?? '',
    city: resolved?.city ?? sourceAddress.city ?? '',
    state: resolved?.state ?? sourceAddress.state ?? '',
    zip: resolved?.zip ?? sourceAddress.zip ?? '',
    latitude: validationResult.latitude,
    longitude: validationResult.longitude,
    validationStatus: 'validated',
    validationMode: 'address',
  };
}

/**
 * Whether an order has a usable jobsite for pickup orders — validated address/coordinates
 * or an override entry with complete address or coordinate data.
 */
export function isOrderJobLocationSatisfied(jobLocation: JobLocation | undefined | null): boolean {
  if (hasValidatedJobLocation(jobLocation)) return true;
  if (jobLocation?.validationStatus === 'override') return jobLocationHasData(jobLocation);
  return false;
}

export function jobLocationHasData(
  jobLocation:
    | {
        address1?: string | null;
        city?: string | null;
        state?: string | null;
        zip?: string | null;
        latitude?: number | null;
        longitude?: number | null;
      }
    | undefined
    | null
): boolean {
  if (!jobLocation) return false;
  const hasAddress = Boolean(
    jobLocation.address1 && jobLocation.city && jobLocation.state && jobLocation.zip
  );
  const hasCoords =
    typeof jobLocation.latitude === 'number' &&
    Number.isFinite(jobLocation.latitude) &&
    typeof jobLocation.longitude === 'number' &&
    Number.isFinite(jobLocation.longitude);
  return hasAddress || hasCoords;
}
